import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { ADMIN_ROLE_LABELS } from "@/lib/roles";
import { EnvironmentSwitcher } from "@/components/environment/EnvironmentSwitcher";

interface TopbarProps {
  onMenuClick?: () => void;
}

const pathLabels: Record<string, string> = {
  "/admin-users": "管理员列表",
  "/admin-users/": "管理员详情",
  ...(__DOMAIN_CRYPTO__ ? {
    "/crypto/overview": "加密运营大盘",
    "/crypto/strategies": "币种策略",
    "/crypto/strategies/": "策略详情",
    "/crypto/models": "模型资源",
    "/crypto/models/": "模型资源详情",
    "/crypto/model-routes": "角色模型路由",
    "/crypto/model-usage": "模型调用统计",
    "/crypto/styles": "AI 决策风格",
    "/crypto/styles/": "风格详情",
    "/crypto/prompts": "共享提示词",
    "/crypto/prompts/": "提示词详情",
    "/crypto/versions": "配置版本",
    "/crypto/versions/": "配置版本详情",
    "/crypto/releases": "发布计划",
    "/crypto/releases/": "发布计划详情",
    "/crypto/alignment": "两端配置对齐",
    "/crypto/agents": "Trade Agent",
    "/crypto/agents/": "Agent 详情",
    "/crypto/users": "用户列表",
    "/crypto/users/": "用户详情",
    "/crypto/decisions": "AI 决策审计",
    "/crypto/decisions/": "完整决策审计",
    "/crypto/orders": "订单意图",
    "/crypto/orders/": "订单执行详情",
    "/crypto/fills": "成交记录",
    "/crypto/positions": "持仓记录",
    "/crypto/plans": "待执行计划",
    "/crypto/runtime-releases": "C 端实际运行版本",
    "/crypto/relations": "配置与 Agent 关系",
    "/crypto/objects/": "配置依赖内容",
  } : {}),
  ...(__DOMAIN_US_STOCK__ ? { "/us-stock": "美股概览" } : {}),
  ...(__DOMAIN_FOREX__ ? { "/forex": "外汇概览" } : {}),
  ...(__DOMAIN_AI__ ? {
    "/ai/dashboard": "数据报表",
    "/ai/reports": "数据报表",
    "/ai/users": "用户管理",
    "/ai/users/": "用户详情",
    "/ai/generations": "生成记录",
    "/ai/generations/": "生成详情",
    "/ai/vip/packages": "VIP 套餐",
    "/ai/vip/subscription-orders": "订阅订单",
    "/ai/vip/subscriptions": "订阅订单",
    "/ai/orders": "全部订单",
    "/ai/points-log": "积分流水",
    "/ai/config": "应用配置",
    "/ai/audit/images": "图片审核",
    "/ai/audit/prompts": "提示词审核",
    "/ai/audit/agent-trace": "Agent 追踪",
    "/ai/users/behavior": "用户行为",
  } : {}),
  ...(__DOMAIN_TIANJI__ ? {
    "/tianji/overview": "天机运营总览",
    "/tianji/users": "天机登录用户",
    "/tianji/orders": "天机订阅订单",
    "/tianji/transactions": "Apple 支付流水",
    "/tianji/subscriptions": "订阅生命周期",
    "/tianji/notifications": "Apple 通知",
    "/tianji/anomalies": "异常与对账",
    "/tianji/usage": "命理与 AI 用量",
    "/tianji/review-accounts": "Apple Review 账号",
    "/tianji/runtime-configs": "天机配置就绪度",
    "/tianji/audit-logs": "天机管理审计",
  } : {}),
};

export default function Topbar({ onMenuClick }: TopbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAdminAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const getLabel = () => {
    const path = location.pathname;
    if (pathLabels[path]) return pathLabels[path];
    for (const key of Object.keys(pathLabels)) {
      if (key.endsWith("/") && path.startsWith(key)) return pathLabels[key];
    }
    return "";
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="h-[72px] bg-white border-b border-surface-border flex items-center justify-between px-4 md:px-6 lg:px-8 shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile hamburger */}
        <button
          onClick={onMenuClick}
          className="lg:hidden w-9 h-9 rounded-lg hover:bg-surface flex items-center justify-center shrink-0 cursor-pointer"
        >
          <i className="ri-menu-line text-text-secondary text-xl" />
        </button>
        <div className="flex items-center gap-2 text-[15px] min-w-0">
          <span className="text-text-muted hidden sm:inline">管理后台</span>
          <i className="ri-arrow-right-s-line text-text-muted text-sm hidden sm:inline" />
          <span className="text-text-primary font-medium truncate">{getLabel()}</span>
        </div>
      </div>
      <div className="flex items-center gap-3 md:gap-5 shrink-0">
        <div className="hidden md:block">
          <EnvironmentSwitcher />
        </div>
        {/* Search */}
        <div className="relative hidden md:block">
          <i className="ri-search-line absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-text-muted text-base" />
          <input
            type="text"
            placeholder="搜索..."
            className="w-[200px] md:w-[280px] lg:w-[320px] h-10 md:h-11 pl-9 md:pl-11 pr-3 md:pr-4 rounded-xl bg-surface text-sm md:text-[15px] text-text-primary placeholder:text-text-muted border border-surface-border focus:border-brand/50 focus:outline-none transition-colors"
          />
        </div>
        <button className="relative w-9 h-9 md:w-11 md:h-11 rounded-xl hover:bg-surface transition-colors flex items-center justify-center cursor-pointer shrink-0">
          <i className="ri-notification-3-line text-text-secondary text-lg md:text-xl" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-status-danger border-2 border-white" />
        </button>

        {/* User Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu((s) => !s)}
            className="flex items-center gap-2 pl-1 pr-1 md:pr-2 py-1.5 rounded-xl hover:bg-surface transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center shrink-0">
              <i className="ri-user-line text-brand text-sm" />
            </div>
            <span className="text-text-secondary text-sm hidden md:block max-w-[100px] lg:max-w-[140px] truncate">
              {admin?.email || "管理员"}
            </span>
            <i className={`ri-arrow-down-s-line text-text-muted text-xs transition-transform hidden md:block ${showUserMenu ? "rotate-180" : ""}`} />
          </button>

          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-[220px] bg-white rounded-2xl border border-surface-border py-2 z-50">
                <div className="px-4 py-3 border-b border-surface-border">
                  <p className="text-text-primary text-sm font-medium truncate">
                    {admin?.email || "管理员"}
                  </p>
                  <p className="text-text-muted text-xs mt-0.5">
                    {admin?.role ? ADMIN_ROLE_LABELS[admin.role] ?? admin.role : ""}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-status-danger hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-logout-box-r-line text-base" />
                  <span>退出登录</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
